import axios from 'axios';
import router from '@/router';
import { ElMessage } from 'element-plus';
import { useUserStore } from '@/stores/userStore';
import { buildAppError } from './appError';

// 创建axios实例
const service = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  timeout: 300000 // 请求超时时间 (5分钟)
});

// 是否正在刷新token
let isRefreshing = false;
// 重试队列
let retryQueue = [];

// 执行队列中的请求
const processQueue = (token, isError = false) => {
  retryQueue.forEach(({ resolve, reject, config }) => {
    if (isError) {
      reject('Token刷新失败');
    } else {
      config.headers.Authorization = `Bearer ${token}`;
      resolve(service(config));
    }
  });
  retryQueue = [];
};

// 内部函数：获取认证头信息
const getAuthHeaders = () => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json'
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

// 内部函数：处理401错误
const handle401Error = async (originalFetch) => {
  const userStore = useUserStore();
  
  if (!isRefreshing) {
    isRefreshing = true;
    
    try {
      const refreshSuccess = await userStore.refreshUserToken();
      if (refreshSuccess) {
        isRefreshing = false;
        return originalFetch();
      } else {
        userStore.logoutUser();
        router.push({
          path: '/',
          query: { showLogin: true, redirect: router.currentRoute.value.fullPath }
        });
        ElMessage.error('登录已过期，请重新登录');
        throw new Error('登录已过期，请重新登录');
      }
    } catch (refreshError) {
      userStore.logoutUser();
      router.push({
        path: '/',
        query: { showLogin: true, redirect: router.currentRoute.value.fullPath }
      });
      ElMessage.error('登录已过期，请重新登录');
      throw new Error('登录已过期，请重新登录');
    } finally {
      isRefreshing = false;
    }
  } else {
    return new Promise((resolve, reject) => {
      const checkAndRetry = () => {
        if (!isRefreshing) {
          originalFetch().then(resolve).catch(reject);
        } else {
          setTimeout(checkAndRetry, 100);
        }
      };
      checkAndRetry();
    });
  }
};

// 流式请求工具函数
const streamRequest = async (url, options = {}) => {
  const {
    method = 'POST',
    body,
    headers: customHeaders = {},
    timeout = 300000,
    ...fetchOptions
  } = options;

  const baseURL = process.env.VUE_APP_API_URL || '';
  const fullUrl = url.startsWith('http') ? url : `${baseURL}${url}`;

  const headers = {
    ...getAuthHeaders(),
    'Accept': 'text/event-stream',
    ...customHeaders
  };

  let processedBody = body;
  if (body instanceof FormData) {
    delete headers['Content-Type'];
  } else if (body && typeof body === 'object') {
    processedBody = JSON.stringify(body);
    if (!headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
  }

  const makeRequest = async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(fullUrl, {
        method,
        headers,
        body: processedBody,
        signal: controller.signal,
        ...fetchOptions
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 401) {
          return handle401Error(() => makeRequest());
        } else if (response.status === 403) {
          const userStore = useUserStore();
          userStore.logoutUser();
          router.push({
            path: '/',
            query: { showLogin: true, redirect: router.currentRoute.value.fullPath }
          });
          throw new Error('没有权限访问该资源');
        } else {
          // 其他错误直接抛出，让上层处理
          const errorData = await response.json().catch(() => ({}));
          const error = new Error(errorData.message || `HTTP error! status: ${response.status}`);
          error.response = { status: response.status, data: errorData };
          throw error;
        }
      }

      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        const timeoutError = new Error('请求超时');
        timeoutError.code = 'TIMEOUT';
        throw timeoutError;
      }
      throw error;
    }
  };

  return makeRequest();
};

// 请求拦截器
service.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    console.error('请求错误', error);
    return Promise.reject(error);
  }
);

// 响应拦截器 - 使用新的错误处理架构
service.interceptors.response.use(
  response => {
    const res = response.data;
    if (res.code !== undefined && res.code !== 0) {
      if (res.message === '成功' || res.success === true) {
        return res;
      }
      return Promise.reject(new Error(res.message || '请求失败'));
    }
    return res;
  },
  async error => {
    // ✅ 1. 规范化错误对象 (核心改进!)
    const appError = buildAppError(error);
    const { httpStatus } = appError;

    // ✅ 2. 只处理全局认证/权限错误，其他交给业务层

    // 2.1 处理401: 区分登录失败 vs token过期
    if (httpStatus === 401) {
      const userStore = useUserStore();
      const originalRequest = error.config;
      const isLoginRequest = originalRequest.url?.includes('/login');

      // 登录接口的401 → 用户名/密码错误，交给组件处理
      if (isLoginRequest) {
        return Promise.reject(appError);
      }

      // 非登录接口的401 → token过期，刷新token
      if (!isRefreshing) {
        isRefreshing = true;

        try {
          const refreshSuccess = await userStore.refreshUserToken();
          if (refreshSuccess) {
            const newToken = localStorage.getItem('token') || sessionStorage.getItem('token');
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            processQueue(newToken);
            return service(originalRequest);
          } else {
            processQueue(null, true);
            userStore.logoutUser();
            router.push({
              path: '/',
              query: { showLogin: true, redirect: router.currentRoute.value.fullPath }
            });
            ElMessage.error('登录已过期，请重新登录');
            return Promise.reject(appError);
          }
        } catch (refreshError) {
          processQueue(null, true);
          userStore.logoutUser();
          router.push({
            path: '/',
            query: { showLogin: true, redirect: router.currentRoute.value.fullPath }
          });
          ElMessage.error('登录已过期，请重新登录');
          return Promise.reject(appError);
        } finally {
          isRefreshing = false;
        }
      } else {
        return new Promise((resolve, reject) => {
          retryQueue.push({ resolve, reject, config: originalRequest });
        });
      }
    }

    // 2.2 处理403: 权限不足
    if (httpStatus === 403) {
      try {
        const userStore = useUserStore();
        userStore.logoutUser();
        router.push({
          path: '/',
          query: { showLogin: true, redirect: router.currentRoute.value.fullPath }
        });
        ElMessage.error('没有权限访问该资源');
      } catch (e) {
        console.error('登出失败', e);
      }
      return Promise.reject(appError);
    }

    // ✅ 3. 其他错误(422/429/500等)不在拦截器显示toast
    //    已经规范化为AppError，包含friendlyMessage
    //    直接抛给业务层，由handleError统一处理

    // 开发环境记录日志
    if (process.env.NODE_ENV === 'development') {
      console.error('HTTP Error:', {
        type: appError.type,
        status: appError.httpStatus,
        code: appError.errorCode,
        message: appError.friendlyMessage,
        url: appError.url
      });
    }

    return Promise.reject(appError);
  }
);

// 文件下载专用函数 - 跳过响应拦截器处理
const downloadRequest = async (url, config = {}) => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const baseURL = process.env.VUE_APP_API_URL || '';
  const fullUrl = url.startsWith('http') ? url : `${baseURL}${url}`;

  const headers = {
    'Authorization': token ? `Bearer ${token}` : '',
    ...config.headers
  };

  console.log(`文件下载请求: ${fullUrl}`);
  console.log(`请求头Authorization: ${headers.Authorization ? headers.Authorization.substring(0, 30) + '...' : 'null'}`);

  const response = await fetch(fullUrl, {
    method: config.method || 'GET',
    headers,
    ...config
  });

  console.log(`下载响应状态: ${response.status} ${response.statusText}`);
  console.log(`响应Content-Type: ${response.headers.get('Content-Type')}`);

  if (!response.ok) {
    const errorText = await response.text();
    console.log(`下载错误响应内容: ${errorText.substring(0, 200)}`);
    
    if (response.status === 401) {
      const userStore = useUserStore();
      userStore.logoutUser();
      router.push({
        path: '/',
        query: { showLogin: true, redirect: router.currentRoute.value.fullPath }
      });
      throw new Error('登录已过期，请重新登录');
    } else if (response.status === 404) {
      throw new Error('文件不存在，可能已过期或生成失败');
    } else if (response.status === 403) {
      throw new Error('没有权限下载此文件');
    }
    
    throw new Error(`下载失败: ${response.status} ${response.statusText}`);
  }

  // 检查响应内容是否为错误页面
  const contentType = response.headers.get('Content-Type');
  if (contentType && (contentType.includes('text/html') || contentType.includes('application/xml'))) {
    const errorText = await response.text();
    console.error('收到HTML/XML响应而不是文件:', errorText.substring(0, 500));
    throw new Error('服务器返回错误页面，请重新生成文件');
  }

  return response;
};

// 便利方法
const request = {
  get: (url, config = {}) => service.get(url, config),
  post: (url, data = {}, config = {}) => service.post(url, data, config),
  put: (url, data = {}, config = {}) => service.put(url, data, config),
  delete: (url, config = {}) => service.delete(url, config),
  patch: (url, data = {}, config = {}) => service.patch(url, data, config),
  download: downloadRequest  // 新增：专门的下载方法
};

export { request, streamRequest };
export default service; 